/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { api } from "api";

// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";

import Card from "@mui/material/Card";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Material Dashboard 2 React Context
import { useMaterialUIController } from "context";

function Dashboard() {
  const [controller, dispatch] = useMaterialUIController();
  const { dateRange } = controller;

  const [menu, setMenu] = useState(null);
  const [showDateRange, setShowDateRange] = useState(false);
  const [loading, setLoading] = useState(false);
  // Ref to track if the fetch was triggered manually by the user (filter)
  // This allows us to skip the full backdrop loader on initial mount if desired
  const filterTriggered = useRef(false);

  const navigate = useNavigate(); // Hook for navigation
  const today = new Date();

  const formatDate = (date) => {
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    const year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  };

  // Dashboard Date Range State - Uses Context now


  // Dashboard Data State
  const [stats, setStats] = useState({
    cashBank: 0,
    netWorkingCapital: 0,
    sales: 0,
    salesJournal: 0,
    salesReturn: 0,
    receipts: 0,
    receivables: 0,
    purchases: 0,
    purchaseReturn: 0,
    payments: 0,
    payables: 0,
    closingCash: 0,
    closingBank: 0,
  });

  // Create User Dialog State Removed
  const compCode = localStorage.getItem("comp_code");
  const [companyName, setCompanyName] = useState(localStorage.getItem("company_name"));

  const fetchDashboardData = async () => {
    if (!compCode) return;

    setLoading(true);

    // Try to fetch company details specifically if name is missing or default
    // Or just always fetch to ensure it's up to date
    if (!companyName || companyName === "Company Name" || true) { // Force check for debugging
      try {
        const companyRes = await api.getCompanyDetails({ compcode: compCode });

        if (companyRes && (companyRes.success || companyRes.data)) {
          const data = companyRes.data || companyRes;
          // Check for name in data or root
          // Check for name in data or root
          const fetchedName = data.company_name || data.companyName || data.CompanyName || data.name || data.Name;

          if (fetchedName) {
            setCompanyName(fetchedName);
            localStorage.setItem("company_name", fetchedName);
          }
        }
      } catch (error) {
        console.error("DEBUG: Failed to fetch company details:", error);
      }
    }

    const fetchData = async (primaryType, alternatives = []) => {
      const typesToTry = [primaryType, ...alternatives];

      for (const vtype of typesToTry) {
        try {
          const res = await api.getTransactions({
            vdatefrom: dateRange.start,
            vdateto: dateRange.end,
            vtype,
            compcode: compCode,
          });

          let total = 0;
          let hasData = false;

          // Check direct amount
          if (res && (res.amount !== undefined && res.amount !== null)) {
            total = Number(res.amount) || 0;
            hasData = true;
          }
          // Check array data (wrapper)
          else if (res && res.data && Array.isArray(res.data)) {
            // Check for company name update
            const potentialName = res.companyName || res.company_name;
            if (potentialName && potentialName !== companyName && potentialName !== "Company Name") {
              // Only update if looks valid
              setCompanyName(potentialName);
              localStorage.setItem("company_name", potentialName);
            }

            total = res.data.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
            hasData = true;
          }
          // Check array data (direct)
          else if (Array.isArray(res)) {
            total = res.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
            hasData = true;
          }

          // If we found data and it's non-zero, return it immediately.
          if (hasData && total !== 0) {
            return total;
          }

          // If valid 0, we still might want to try alternatives just in case the "0" was a "no results found" default from a silent API failure/mismatch.
          // We continue loop.
        } catch (e) {
          console.error(`Error fetching ${vtype}`, e);
        }
      }
      return 0;
    };

    // Parallel fetching with robust fallbacks for multi-word types
    // Parallel fetching with robust fallbacks for multi-word types
    const [
      sales,
      salesJournal,
      salesRet,
      purchases,
      purchaseJournal,
      purchRet,
      receipts,
      payments,
      payables,
      receivables,
      closingBalances,
    ] = await Promise.all([
      fetchData("SALES"),
      fetchData("SALES JOURNAL", ["Sales Journal", "SALES_JOURNAL"]),
      fetchData("SALES RETURN", ["SALESRETURN", "Sales Return", "SALES_RETURN"]),
      fetchData("PURCHASE"),
      fetchData("PURCHASE JOURNAL", ["Purchase Journal", "PURCHASE_JOURNAL"]),
      fetchData("PURCHASE RETURN", ["PURCHASERETURN", "Purchase Return", "PURCHASE_RETURN"]),
      fetchData("RECEIPT", ["RECIEPT", "Receipt", "RECEIPTS"]),
      fetchData("PAYMENT", ["PAYMENTS"]),
      fetchData("PAYABLE"),
      fetchData("RECEIVABLE", ["RECIEVABLE", "Receivable"]),
      api.getClosingCashBank({ companyCode: compCode, asOnDate: dateRange.end }).catch(e => {
        console.warn("Failed to fetch closing cash/bank", e);
        return [];
      }),
    ]);


    // Parse Closing Balances
    let closedCash = 0;
    let closedBank = 0;
    if (Array.isArray(closingBalances)) {
      closingBalances.forEach(item => {
        if (item.accGroup === "Cash") closedCash += item.closingAmount;
        if (item.accGroup === "Banks") closedBank += item.closingAmount;
      });
    }

    setStats({
      sales,
      salesJournal,
      salesReturn: salesRet,
      purchases,
      purchaseJournal,
      purchaseReturn: purchRet,
      receipts,
      payments,
      payables,
      receivables,
      cashBank: closedCash + closedBank, // Total Liquidity
      closingCash: closedCash,
      closingBank: closedBank,
      netWorkingCapital: (receivables || 0) - (payables || 0), // Explicit calc
    });

    // Always clear loading, and reset trigger
    setLoading(false);
    filterTriggered.current = false;
  };

  useEffect(() => {
    fetchDashboardData();
  }, [compCode, dateRange]);





  const openMenu = ({ currentTarget }) => setMenu(currentTarget);
  const closeMenu = () => setMenu(null);

  const handleDateFilter = (type) => {
    const today = new Date();
    let start = new Date();
    let end = new Date();



    if (type === "today") {
      start = today;
      end = today;
    } else if (type === "yesterday") {
      const y = new Date(today);
      y.setDate(today.getDate() - 1);
      start = y;
      end = y;
    } else if (type === "this_month") {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = today;
    } else if (type === "last_month") {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    } else if (type === "this_year") {
      start = new Date(today.getFullYear(), 0, 1);
      end = today;
    }

    const formatDate = (date) => {
      const d = new Date(date);
      let month = "" + (d.getMonth() + 1);
      let day = "" + d.getDate();
      const year = d.getFullYear();

      if (month.length < 2) month = "0" + month;
      if (day.length < 2) day = "0" + day;

      return [year, month, day].join("-");
    };

    // Mark as user-triggered filter
    filterTriggered.current = true;
    dispatch({ type: "DATE_RANGE", value: { start: formatDate(start), end: formatDate(end), type } });
    setShowDateRange(false);
    closeMenu();
  };

  const renderMenu = (
    <Menu
      id="simple-menu"
      anchorEl={menu}
      anchorOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      open={Boolean(menu)}
      onClose={closeMenu}
    >
      <MenuItem onClick={() => handleDateFilter("today")} selected={dateRange.type === "today"} style={{ backgroundColor: dateRange.type === "today" ? "#e3f2fd" : "inherit" }}>Today</MenuItem>
      <MenuItem onClick={() => handleDateFilter("yesterday")} selected={dateRange.type === "yesterday"} style={{ backgroundColor: dateRange.type === "yesterday" ? "#e3f2fd" : "inherit" }}>Yesterday</MenuItem>
      <MenuItem onClick={() => handleDateFilter("this_month")} selected={dateRange.type === "this_month"} style={{ backgroundColor: dateRange.type === "this_month" ? "#e3f2fd" : "inherit" }}>This Month</MenuItem>
      <MenuItem onClick={() => handleDateFilter("last_month")} selected={dateRange.type === "last_month"} style={{ backgroundColor: dateRange.type === "last_month" ? "#e3f2fd" : "inherit" }}>Last Month</MenuItem>
      <MenuItem onClick={() => handleDateFilter("this_year")} selected={dateRange.type === "this_year"} style={{ backgroundColor: dateRange.type === "this_year" ? "#e3f2fd" : "inherit" }}>This Year</MenuItem>
      <MenuItem
        onClick={() => {
          setShowDateRange(true);
          dispatch({ type: "DATE_RANGE", value: { ...dateRange, type: "custom" } });
          closeMenu();
        }}
        selected={dateRange.type === "custom"}
        style={{ backgroundColor: dateRange.type === "custom" ? "#e3f2fd" : "inherit" }}
      >
        Select Date Range
      </MenuItem>
    </Menu>
  );

  // Helper for Solid Cards
  // eslint-disable-next-line react/prop-types
  const SolidCard = ({ color, title, count }) => (
    <Card>
      <MDBox
        variant="gradient"
        bgColor={color}
        borderRadius="xl"
        coloredShadow={color}
        p={3}
      >
        <MDTypography variant="caption" color="white" fontWeight="bold" opacity={0.8} textTransform="uppercase" display="block">
          {title}
        </MDTypography>
        <MDTypography variant="h4" color="white" fontWeight="bold" mt={1}>
          {count}
        </MDTypography>
      </MDBox>
    </Card>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar showTitle={false} companyName={companyName} />
      <MDBox py={1}>
        {/* Header Section */}
        {/* Header Section */}
        <MDBox mb={1} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
          <MDBox>
            <MDTypography
              variant="h6"
              fontWeight="regular"
              color="text"
              sx={{
                letterSpacing: "0.5px",
                fontFamily: ' "Roboto", "Helvetica", "Arial", sans-serif',
                opacity: 0.8,
              }}
            >
              Company ID: {compCode || "N/A"}
            </MDTypography>
          </MDBox>

          <MDBox color="text" display="flex" alignItems="center" ml="auto">
            {showDateRange && (
              <MDBox display="flex" alignItems="center" mr={1}>
                <MDInput
                  type="date"
                  variant="outlined"
                  size="small"
                  sx={{ mr: 1, backgroundColor: "white" }}
                  value={dateRange.start}
                  inputProps={{ max: dateRange.end }}
                  onChange={(e) => dispatch({ type: "DATE_RANGE", value: { ...dateRange, start: e.target.value, type: "custom" } })}
                />
                <MDTypography variant="caption" sx={{ mx: 1 }}>
                  to
                </MDTypography>
                <MDInput
                  type="date"
                  variant="outlined"
                  size="small"
                  sx={{ backgroundColor: "white" }}
                  value={dateRange.end}
                  inputProps={{ min: dateRange.start }}
                  onChange={(e) => dispatch({ type: "DATE_RANGE", value: { ...dateRange, end: e.target.value, type: "custom" } })}
                />
              </MDBox>
            )}
            <IconButton
              size="small"
              disableRipple
              color="inherit"
              sx={{
                ml: 1,
                boxShadow:
                  "0rem 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1), 0rem 0.125rem 0.25rem -0.0625rem rgba(0, 0, 0, 0.06)",
                backgroundColor: "#fff",
                "&:hover": {
                  backgroundColor: "#f8f9fa",
                },
              }}
              onClick={openMenu}
            >
              <Icon fontSize="medium" color="inherit">
                filter_list
              </Icon>
            </IconButton>
            {renderMenu}
          </MDBox>
        </MDBox>




        {/* Core Transaction Flow */}
        <MDBox mb={1}>
          <MDTypography variant="h5" fontWeight="bold" color="dark">
            Balances
          </MDTypography>
        </MDBox>
        <Grid container spacing={3} mb={1}>
          <Grid item xs={12} md={6}>
            <MDBox
              mb={1.5}
              onClick={() => navigate("/sales", {
                state: {
                  title: "Cash & Bank Balance Details",
                  type: "Cash & Bank",
                  dateRange,
                  compCode,
                  extras: {
                    total: stats.cashBank,
                    cash: stats.closingCash,
                    bank: stats.closingBank
                  }
                }
              })}
              sx={{
                cursor: "pointer",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "scale(1.02)",
                },
              }}
            >
              {/* Custom Teal Card */}
              <Card>
                <MDBox
                  variant="gradient"
                  bgColor="info" // using info (blue/teal) as approximation or custom hex
                  borderRadius="xl"
                  coloredShadow="info"
                  p={3}
                  sx={{ background: "linear-gradient(195deg, #009688 0%, #00897b 100%)" }} // Override for Teal
                >
                  <MDTypography variant="caption" color="white" fontWeight="bold" opacity={0.8} textTransform="uppercase" display="block">
                    Total Cash & Bank Balance
                  </MDTypography>
                  <MDTypography variant="h3" color="white" fontWeight="bold" mt={1}>
                    QAR {stats.cashBank.toLocaleString()}
                  </MDTypography>
                </MDBox>
              </Card>
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6}>
            <MDBox mb={1.5}>
              {/* Net Working Capital - Purple */}
              <Card>
                <MDBox
                  variant="gradient"
                  borderRadius="xl"
                  coloredShadow="primary"
                  p={3}
                  sx={{ background: "linear-gradient(195deg, #7b1fa2 0%, #7b1fa2 100%)" }} // Purple
                >
                  <MDTypography variant="caption" color="white" fontWeight="bold" opacity={0.8} textTransform="uppercase" display="block">
                    Net Working Capital (AR - AP)
                  </MDTypography>
                  <MDTypography variant="h3" color="white" fontWeight="bold" mt={1}>
                    QAR {stats.netWorkingCapital.toLocaleString()}
                  </MDTypography>
                </MDBox>
              </Card>
            </MDBox>
          </Grid>
        </Grid>

        <MDBox mb={1}>
          <MDTypography variant="h5" fontWeight="bold" color="dark">
            Core Transaction Flow
          </MDTypography>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox
              mb={1.5}
              onClick={() => navigate("/sales", { state: { sales: stats.sales, salesJournal: stats.salesJournal, title: "Sales Breakdown", type: "Sales", dateRange, compCode } })}
              sx={{
                cursor: "pointer",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            >
              <SolidCard color="success" title="SALES" count={`QAR ${((Number(stats.sales) || 0) + (Number(stats.salesJournal) || 0)).toLocaleString()}`} />
            </MDBox>
          </Grid>
          {/* Sales Journal Card Removed */}
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}
              onClick={() => navigate("/sales", { state: { title: "Sales Return", type: "Sales Return", dateRange, compCode } })}
              sx={{ cursor: "pointer", transition: "transform 0.2s", "&:hover": { transform: "scale(1.05)" } }}
            >
              <SolidCard color="secondary" title="SALES RETURN" count={`QAR ${(Number(stats.salesReturn) || 0).toLocaleString()}`} />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}
              onClick={() => navigate("/sales", { state: { title: "Receipt", type: "Receipt", dateRange, compCode } })}
              sx={{ cursor: "pointer", transition: "transform 0.2s", "&:hover": { transform: "scale(1.05)" } }}
            >
              <SolidCard color="warning" title="RECEIPT" count={`QAR ${(Number(stats.receipts) || 0).toLocaleString()}`} />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}
              onClick={() => navigate("/sales", { state: { title: "Receivable", type: "Receivable", dateRange, compCode } })}
              sx={{ cursor: "pointer", transition: "transform 0.2s", "&:hover": { transform: "scale(1.05)" } }}
            >
              <SolidCard color="info" title="RECEIVABLE" count={`QAR ${(Number(stats.receivables) || 0).toLocaleString()}`} />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox
              mb={1.5}
              onClick={() => navigate("/sales", { state: { sales: stats.purchases, salesJournal: stats.purchaseJournal, title: "Purchase Breakdown", type: "Purchase", dateRange, compCode } })}
              sx={{
                cursor: "pointer",
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "scale(1.05)",
                },
              }}
            >
              <SolidCard color="error" title="PURCHASE" count={`QAR ${((Number(stats.purchases) || 0) + (Number(stats.purchaseJournal) || 0)).toLocaleString()}`} />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}
              onClick={() => navigate("/sales", { state: { title: "Purchase Return", type: "Purchase Return", dateRange, compCode } })}
              sx={{ cursor: "pointer", transition: "transform 0.2s", "&:hover": { transform: "scale(1.05)" } }}
            >
              <SolidCard color="primary" title="PURCHASE RETURN" count={`QAR ${(Number(stats.purchaseReturn) || 0).toLocaleString()}`} />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}
              onClick={() => navigate("/sales", { state: { title: "Payment", type: "Payment", dateRange, compCode } })}
              sx={{ cursor: "pointer", transition: "transform 0.2s", "&:hover": { transform: "scale(1.05)" } }}
            >
              <SolidCard color="error" title="PAYMENT" count={`QAR ${(Number(stats.payments) || 0).toLocaleString()}`} />
            </MDBox>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <MDBox mb={1.5}
              onClick={() => navigate("/sales", { state: { title: "Payable", type: "Payable", dateRange, compCode } })}
              sx={{ cursor: "pointer", transition: "transform 0.2s", "&:hover": { transform: "scale(1.05)" } }}
            >
              <SolidCard color="dark" title="PAYABLE" count={`QAR ${(Number(stats.payables) || 0).toLocaleString()}`} />
            </MDBox>
          </Grid>
        </Grid>



        {/* Create User Dialog */}

      </MDBox>
      <Footer />
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </DashboardLayout >
  );
}

export default Dashboard;
